import { useEffect, useState } from 'react';
import { Star } from 'lucide-react';
import { Heading } from './Heading';
import { spacingClass } from '@/utils/spacing';

function initials(name = '') {
  return name.trim().split(/\s+/).slice(0, 2).map((w) => w[0]?.toUpperCase() || '').join('');
}

function Stars({ rating = 5, size = 14 }) {
  const rounded = Math.round(rating);
  return (
    <div className="flex items-center gap-0.5" aria-label={`${rating} su 5 stelle`}>
      {[1, 2, 3, 4, 5].map((n) => (
        <Star key={n} size={size} className={n <= rounded ? 'fill-theme-accent text-theme-accent' : 'text-theme-border'} />
      ))}
    </div>
  );
}

/**
 * Widget recensioni Google: elenco recensioni curato in admin (nessuna
 * chiamata live a Google — l'API Place Details ha quota/costo e limita a 5
 * recensioni non scelte da noi, in contrasto con "ultime recensioni"
 * configurabile), voto medio in testa, più un pannello con QR code e CTA
 * che portano al link di recensione Google (Impostazioni → non serve,
 * campo `reviewLink` di questo blocco). Il QR è generato lato client (pacchetto
 * `qrcode`, import dinamico come Leaflet in MapBlock.jsx — il bundle di
 * produzione lo paga solo se il blocco è in pagina) quindi non compare nel
 * pre-render statico per i bot: irrilevante, non è contenuto testuale/SEO.
 * @param {{ title?: string, subtitle?: string, rating?: number, reviewCount?: number, reviews?: Array<{ avatar?: string, author?: string, rating?: number, date?: string, text?: string }>, reviewLink?: string, ctaLabel?: string, showQrCode?: boolean, spacing?: string, headingLevel?: number }} props
 */
export function GoogleReviewsBlock({
  title, subtitle, rating = 4.9, reviewCount = 0, reviews = [],
  reviewLink = '', ctaLabel, showQrCode = true, spacing = 'normal', headingLevel = 2,
}) {
  const [qrDataUrl, setQrDataUrl] = useState('');

  useEffect(() => {
    if (!showQrCode || !reviewLink) { setQrDataUrl(''); return undefined; }
    let cancelled = false;
    import('qrcode').then((mod) => {
      const QRCode = mod.default || mod;
      const accent = getComputedStyle(document.documentElement).getPropertyValue('--color-accent').trim() || '#8C6240';
      return QRCode.toDataURL(reviewLink, { width: 240, margin: 1, color: { dark: accent, light: '#FFFFFFFF' } });
    }).then((url) => { if (!cancelled && url) setQrDataUrl(url); }).catch(() => {});
    return () => { cancelled = true; };
  }, [showQrCode, reviewLink]);

  const showPanel = showQrCode || ctaLabel;

  return (
    <section className={'px-6 md:px-16 ' + spacingClass(spacing)}>
      <div className="max-w-6xl mx-auto">
        {(title || subtitle || rating) && (
          <div className="max-w-xl mb-14">
            {title && <Heading level={headingLevel} className="font-heading text-h1 font-light text-theme-text mb-4">{title}</Heading>}
            {subtitle && <p className="text-body-lg text-theme-text-muted leading-relaxed mb-4">{subtitle}</p>}
            {rating > 0 && (
              <div className="flex items-center gap-2">
                <Stars rating={rating} size={18} />
                <span className="text-sm text-theme-text-muted">
                  {rating.toFixed(1)} su Google{reviewCount > 0 ? ` · ${reviewCount} recensioni` : ''}
                </span>
              </div>
            )}
          </div>
        )}

        <div className={'grid gap-8 ' + (showPanel ? 'lg:grid-cols-3' : '')}>
          <div className={'grid grid-cols-1 sm:grid-cols-2 gap-6 ' + (showPanel ? 'lg:col-span-2' : '')}>
            {reviews.map((r, i) => (
              <div key={i} className="rounded-2xl border border-theme-border bg-theme-bg p-6">
                <div className="flex items-center gap-3 mb-3">
                  {r.avatar ? (
                    <img src={r.avatar} alt="" className="w-10 h-10 rounded-full object-cover" loading="lazy" decoding="async" />
                  ) : (
                    <div className="w-10 h-10 rounded-full bg-theme-bg-subtle flex items-center justify-center text-xs font-medium text-theme-text-muted">
                      {initials(r.author)}
                    </div>
                  )}
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-theme-text truncate">{r.author}</p>
                    {r.date && <p className="text-xs text-theme-text-subtle">{r.date}</p>}
                  </div>
                </div>
                <Stars rating={r.rating ?? 5} />
                {r.text && <p className="text-sm text-theme-text-muted leading-relaxed mt-3">{r.text}</p>}
              </div>
            ))}
          </div>

          {showPanel && (
            <div className="lg:col-span-1 lg:sticky lg:top-24 self-start rounded-2xl border border-theme-border bg-theme-bg-subtle p-8 text-center flex flex-col items-center gap-4">
              {qrDataUrl && <img src={qrDataUrl} alt="QR code per lasciare una recensione Google" width={160} height={160} className="rounded-lg" />}
              {!qrDataUrl && showQrCode && !reviewLink && import.meta.env?.DEV && (
                <p className="text-xs text-theme-text-subtle">QR code: imposta "Link recensione Google" per generarlo.</p>
              )}
              <p className="text-sm text-theme-text-muted">Inquadra il QR o tocca il pulsante per lasciarci la tua recensione.</p>
              {ctaLabel && (
                <a
                  href={reviewLink || '#'}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center px-8 py-3.5 rounded-lg bg-theme-accent text-theme-accent-text text-sm font-medium tracking-wide"
                >
                  {ctaLabel}
                </a>
              )}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
